import { useQuery } from '@tanstack/react-query';
import { ExternalUiVaultConfig, VaultStats } from 'src/@types/vaults';
import { UiVaults } from 'src/constants/vaults';
import useGetOraclePriceForMarket from 'src/hooks/useGetOraclePriceForMarket';
import { useVaultsStore } from 'src/stores/vaultsStore/useVaultsStore';
import { VAULT_REFETCH_INTERVAL } from '../consts';
import { vaultQueryKeys } from '../vaultQueryKeys';

export type ExternalVaultStatsLookup = Record<string, VaultStats>;

async function fetchExternalVaultStats(
	externalVaults: ExternalUiVaultConfig[],
	getOraclePriceForMarket: ReturnType<typeof useGetOraclePriceForMarket>
): Promise<ExternalVaultStatsLookup> {
	const vaultStatsPromises = externalVaults.map(async (uiVaultConfig) => {
		const vaultStats = await uiVaultConfig.externalDataApiAdapter.getVaultStats(
			getOraclePriceForMarket
		);
		return [uiVaultConfig.vaultPubkeyString, vaultStats] as [
			string,
			VaultStats,
		];
	});

	const vaultStatsList = await Promise.all(vaultStatsPromises);
	const lookup: ExternalVaultStatsLookup = {};

	vaultStatsList.forEach(([vaultPubkey, vaultStats]) => {
		lookup[vaultPubkey] = vaultStats;
	});

	return lookup;
}

export function useExternalVaultStats() {
	const getOraclePriceForMarket = useGetOraclePriceForMarket();
	const areVaultConfigsLoaded = useVaultsStore((s) => s.areVaultConfigsLoaded);

	return useQuery({
		queryKey: vaultQueryKeys.externalVaultStats(),
		queryFn: () => {
			const externalVaults =
				UiVaults.getExternalVaults() as ExternalUiVaultConfig[];
			return fetchExternalVaultStats(externalVaults, getOraclePriceForMarket);
		},
		enabled: areVaultConfigsLoaded,
		staleTime: VAULT_REFETCH_INTERVAL,
		refetchInterval: VAULT_REFETCH_INTERVAL,
	});
}
